import React, { useState } from 'react';
import styled from 'styled-components';

const BLACK_COLOR = '#000000';
const CREAM_COLOR = '#fff';

const Form = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${BLACK_COLOR};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${BLACK_COLOR};
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  width: 18px;
  height: 18px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 14px 28px;
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.secondary ? BLACK_COLOR : CREAM_COLOR};
  background-color: ${props => props.secondary ? CREAM_COLOR : BLACK_COLOR};
  border: 2px solid ${BLACK_COLOR};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.secondary ? CREAM_COLOR : BLACK_COLOR};
    background-color: ${props => props.secondary ? BLACK_COLOR : CREAM_COLOR};
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
`;

const PaymentMethodForm = ({ onSubmit, onCancel, initialData = {}, isEditing = false }) => {
  const [formData, setFormData] = useState({
    cardholderName: initialData.cardholderName || '',
    cardNumber: initialData.cardNumber || '',
    expiryMonth: initialData.expiryMonth || '',
    expiryYear: initialData.expiryYear || '',
    cvv: initialData.cvv || '',
    type: initialData.type || 'VISA',
    isDefault: initialData.default || false
  });
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'cardNumber') {
      // Format card number with spaces every 4 digits
      const formattedValue = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Basic validation
    if (!formData.cardholderName || !formData.cardNumber || !formData.expiryMonth || 
        !formData.expiryYear || !formData.cvv) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Format card data for storage
    const cardNumber = formData.cardNumber.replace(/\s/g, '');
    if (cardNumber.length < 15) {
      setError('Please enter a valid card number');
      return;
    }
    
    const last4 = cardNumber.slice(-4);
    const expiry = `${formData.expiryMonth}/${formData.expiryYear}`;
    
    setLoading(true);
    
    try {
      await onSubmit({
        cardholderName: formData.cardholderName,
        last4,
        expiry,
        type: formData.type,
        default: formData.isDefault
      });
    } catch (err) {
      setError(err.message || 'An error occurred while saving the payment method');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate month options
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return (
      <option key={month} value={month.toString().padStart(2, '0')}>
        {month.toString().padStart(2, '0')}
      </option>
    );
  });
  
  // Generate year options (current year + 10 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = currentYear + i;
    return (
      <option key={year} value={year.toString().slice(-2)}>
        {year}
      </option>
    );
  });
  
  return (
    <Form onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <FormGroup>
        <Label htmlFor="cardholderName">Cardholder Name*</Label>
        <Input
          type="text"
          id="cardholderName"
          name="cardholderName"
          value={formData.cardholderName}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="cardNumber">Card Number*</Label>
        <Input
          type="text"
          id="cardNumber"
          name="cardNumber"
          value={formData.cardNumber}
          onChange={handleChange}
          placeholder="XXXX XXXX XXXX XXXX"
          maxLength="19"
          required
        />
      </FormGroup>
      
      <Row>
        <FormGroup style={{ flex: 2 }}>
          <Label htmlFor="expiry">Expiration Date*</Label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Select
              id="expiryMonth"
              name="expiryMonth"
              value={formData.expiryMonth}
              onChange={handleChange}
              required
            >
              <option value="">Month</option>
              {monthOptions}
            </Select>
            <Select
              id="expiryYear"
              name="expiryYear"
              value={formData.expiryYear}
              onChange={handleChange}
              required
            >
              <option value="">Year</option>
              {yearOptions}
            </Select>
          </div>
        </FormGroup>
        
        <FormGroup style={{ flex: 1 }}>
          <Label htmlFor="cvv">CVV*</Label>
          <Input
            type="text"
            id="cvv"
            name="cvv"
            value={formData.cvv}
            onChange={handleChange}
            maxLength="4"
            required
          />
        </FormGroup>
      </Row>
      
      <FormGroup>
        <Label htmlFor="type">Card Type*</Label>
        <Select
          id="type"
          name="type"
          value={formData.type}
          onChange={handleChange}
          required
        >
          <option value="VISA">Visa</option>
          <option value="MASTERCARD">Mastercard</option>
          <option value="AMEX">American Express</option>
          <option value="DISCOVER">Discover</option>
        </Select>
      </FormGroup>
      
      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id="isDefault"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
        />
        <Label htmlFor="isDefault" style={{ display: 'inline', marginBottom: 0 }}>
          Set as default payment method
        </Label>
      </CheckboxContainer>
      
      <ButtonGroup>
        <Button type="button" secondary onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : isEditing ? 'Update Payment Method' : 'Add Payment Method'}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default PaymentMethodForm;
